const BASE_URL = process.env.REACT_APP_API_URL || '';

function getAuthHeader() {
  const creds = localStorage.getItem('facturapp_credentials');
  if (!creds) return {};
  return { Authorization: `Basic ${creds}` };
}

async function request(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  const headers = {
    ...getAuthHeader(),
    ...options.headers,
  };

  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers,
    cache: 'no-store',
  });

  if (response.status === 401) {
    localStorage.removeItem('facturapp_credentials');
    localStorage.removeItem('facturapp_user');
    window.location.reload();
    throw new Error('No autorizado');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Error ${response.status}`);
  }

  if (response.status === 204) return null;

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return response;
}

function buildQuery(params) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => query.append(key, v));
      } else {
        query.append(key, value);
      }
    }
  });
  const str = query.toString();
  return str ? `?${str}` : '';
}

// Auth
export async function login(username, password) {
  const creds = btoa(`${username}:${password}`);
  const response = await fetch(`${BASE_URL}/api/auth/login`, {
    headers: { Authorization: `Basic ${creds}` },
  });
  if (!response.ok) throw new Error('Credenciales incorrectas');
  localStorage.setItem('facturapp_credentials', creds);
  localStorage.setItem('facturapp_user', username);
  return { username };
}

export function logout() {
  localStorage.removeItem('facturapp_credentials');
  localStorage.removeItem('facturapp_user');
}

export function isAuthenticated() {
  return !!localStorage.getItem('facturapp_credentials');
}

export function getUsername() {
  return localStorage.getItem('facturapp_user') || '';
}

// Dashboard
export function getDashboard() {
  return request('/api/stats/dashboard');
}

// Facturas
export function getFacturas(filters = {}) {
  return request(`/api/facturas${buildQuery(filters)}`);
}

export function getFactura(id) {
  return request(`/api/facturas/${id}`);
}

export function updateFactura(id, data) {
  return request(`/api/facturas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteFactura(id) {
  return request(`/api/facturas/${id}`, { method: 'DELETE' });
}

export function validarFactura(id) {
  return request(`/api/facturas/${id}/validar`, { method: 'PUT' });
}

export function reprocesarFactura(id) {
  return request(`/api/facturas/${id}/reprocesar`, { method: 'POST' });
}

export function searchFacturas(query) {
  return request(`/api/facturas${buildQuery({ q: query })}`);
}

// Upload
export function uploadFacturas(files) {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return request('/api/facturas/upload', {
    method: 'POST',
    body: formData,
  });
}

// Proveedores
export function getProveedores() {
  return request('/api/proveedores');
}

export function createProveedor(data) {
  return request('/api/proveedores', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateProveedor(id, data) {
  return request(`/api/proveedores/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteProveedor(id) {
  return request(`/api/proveedores/${id}`, { method: 'DELETE' });
}

// IMAP Config
export function getImapConfig() {
  return request('/api/config/imap');
}

export function updateImapConfig(data) {
  return request('/api/config/imap', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function testImapConnection() {
  return request('/api/config/imap/test', { method: 'POST' });
}

export function triggerImapPoll({ searchAll = false } = {}) {
  const params = searchAll ? '?search_all=true' : '';
  return request(`/api/config/imap/poll${params}`, { method: 'POST' });
}

export function getImapFolders() {
  return request('/api/config/imap/folders');
}

export function getImapPendientes(estado) {
  const q = estado ? `?estado=${estado}` : '';
  return request(`/api/config/imap/pendientes${q}`);
}

export function getImapPendientesCount() {
  return request('/api/config/imap/pendientes/count');
}

export function updateImapPendiente(id, estado, notas) {
  const params = new URLSearchParams({ estado });
  if (notas) params.append('notas', notas);
  return request(`/api/config/imap/pendientes/${id}?${params}`, { method: 'PUT' });
}

// Categorias
export function getCategorias() {
  return request('/api/config/categorias');
}

export function createCategoria(data) {
  return request('/api/config/categorias', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// API Keys config
export function getKeysConfig() {
  return request('/api/config/keys');
}

export function updateKeysConfig(data) {
  return request('/api/config/keys', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Admin credentials
export function getAdminConfig() {
  return request('/api/config/admin');
}

export function updateAdminConfig(data) {
  return request('/api/config/admin', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Export
export async function exportExcel(filters = {}) {
  const response = await request(`/api/export/excel${buildQuery(filters)}`);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `facturas_${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Batch validation
export function batchValidarFacturas(ids) {
  const params = ids.map(id => `ids=${id}`).join('&');
  return request(`/api/facturas/batch/validar?${params}`, { method: 'PUT' });
}

// Duplicates
export function getFacturasDuplicadas() {
  return request('/api/facturas/duplicados');
}

// Monthly summary
export function getResumenMensual(year, month) {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (month) params.append('month', month);
  const q = params.toString();
  return request(`/api/stats/resumen-mensual${q ? '?' + q : ''}`);
}

// IMAP log
export function getImapLog(limit = 50) {
  return request(`/api/config/imap/log?limit=${limit}`);
}

export function clearImapLog() {
  return request('/api/config/imap/log', { method: 'DELETE' });
}

// CSV export
export async function exportCSV(filters = {}) {
  const result = await getFacturas({ ...filters, per_page: 200 });
  const items = result.items || [];
  if (items.length === 0) return;

  const headers = ['N.Factura', 'Fecha', 'Proveedor', 'Base Imponible', '% IVA', 'Cuota IVA', 'Total', 'Categoria', 'Estado'];
  const rows = items.map(f => [
    f.numero_factura || '',
    f.fecha_factura || '',
    f.proveedor_nombre || '',
    f.base_imponible ?? '',
    f.tipo_iva ?? '',
    f.cuota_iva ?? '',
    f.total ?? '',
    f.categoria || '',
    f.estado || '',
  ]);

  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `facturas_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// PDF
export function getFacturaPdfUrl(id) {
  const creds = localStorage.getItem('facturapp_credentials');
  return `${BASE_URL}/api/facturas/${id}/pdf?auth=${encodeURIComponent(creds || '')}`;
}
